import { CheckCircle, ExternalLink, Link } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsSitelinkManagement,
  type GoogleAdsSitelinkManagementResult,
} from 'sprites-types/workflow/tools/googleAdsSitelinkManagement'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleAdsSitelinkManagementDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsSitelinkManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsSitelinkManagementResult
  const { linkTarget, sitelinkId } = result

  // Get input data from nodeExecution
  const inputs = nodeExecution.inputs as any
  const linkText = inputs?.functionArgs?.action?.params?.linkText || ''
  const finalUrl = inputs?.functionArgs?.action?.params?.finalUrl || ''
  const description1 = inputs?.functionArgs?.action?.params?.description1
  const description2 = inputs?.functionArgs?.action?.params?.description2
  const targetId =
    inputs?.functionArgs?.action?.params?.campaignId ||
    inputs?.functionArgs?.action?.params?.assetGroupId ||
    inputs?.functionArgs?.action?.params?.adGroupId ||
    ''

  const targetType =
    linkTarget === 'assetGroup' ? 'Asset Group' : linkTarget === 'adGroup' ? 'Ad Group' : 'Campaign'

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3.5">
        <div className="mt-0.5 flex-shrink-0">
          <div className="rounded-full bg-gradient-to-br from-blue-500 to-blue-600 p-2.5 shadow-lg">
            <Link className="h-5 w-5 text-white" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">Sitelink Extension Added</div>
            <div className="ml-auto">
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Active
              </span>
            </div>
          </div>

          {/* Preview Card */}
          <div className="mb-3 rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-3 shadow-sm">
            <div className="mb-2 flex items-start gap-2">
              <ExternalLink className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
              <div className="min-w-0 flex-1">
                <div className="mb-1 text-base font-semibold text-blue-600">{linkText}</div>
                <div className="break-all text-xs text-gray-600">{finalUrl}</div>
              </div>
            </div>

            {(description1 || description2) && (
              <div className="mt-3 space-y-1 border-t border-gray-200 pt-2">
                {description1 && (
                  <div className="text-xs leading-relaxed text-gray-700">{description1}</div>
                )}
                {description2 && (
                  <div className="text-xs leading-relaxed text-gray-700">{description2}</div>
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="rounded-lg bg-gray-50 px-3 py-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-500">Target:</span>
                <span className="ml-1.5 text-gray-900">{targetType}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">ID:</span>
                <span className="ml-1.5 font-mono text-gray-900">{targetId}</span>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-500">Sitelink ID:</span>
                <span className="ml-1.5 font-mono text-gray-900">{sitelinkId}</span>
              </div>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
            <span>Extension is live and will appear with your ads</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsSitelinkManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsSitelinkManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsSitelinkManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsSitelinkManagementResult
  const { linkTarget, sitelinkId } = result

  // Get input data from nodeExecution
  const inputs = nodeExecution.inputs as any
  const linkText = inputs?.functionArgs?.action?.params?.linkText || ''
  const finalUrl = inputs?.functionArgs?.action?.params?.finalUrl || ''
  const description1 = inputs?.functionArgs?.action?.params?.description1
  const description2 = inputs?.functionArgs?.action?.params?.description2
  const targetId =
    inputs?.functionArgs?.action?.params?.campaignId ||
    inputs?.functionArgs?.action?.params?.assetGroupId ||
    inputs?.functionArgs?.action?.params?.adGroupId ||
    ''

  let markdown = `## Google Ads Sitelink Added

**Link Text:** ${linkText}
**Destination URL:** ${finalUrl}
`

  if (description1) {
    markdown += `**Description 1:** ${description1}\n`
  }
  if (description2) {
    markdown += `**Description 2:** ${description2}\n`
  }

  const targetType =
    linkTarget === 'assetGroup' ? 'Asset Group' : linkTarget === 'adGroup' ? 'Ad Group' : 'Campaign'

  markdown += `**${targetType} ID:** ${targetId}
**Sitelink ID:** ${sitelinkId}

---
`

  return markdown
}

export const GoogleAdsSitelinkManagementDisplay = createWorkflowToolComponent(
  GoogleAdsSitelinkManagementDisplayComponent,
  downloadGoogleAdsSitelinkManagement,
  googleAdsSitelinkManagementToMarkdown
)
